import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Plus, Save, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Confirm, Empty, Field, FilePickButton, IconBtn, Loading, Page, SearchBox, SuggestField } from "../components/wf";
import { api, fileUrl, money, uploadFile } from "../lib/api";
import { usePerms } from "../lib/auth";
import { useParts, useSuggestions } from "../lib/queries";
import { DraftBanner, SaveStatus, useAutoSave, useDraft } from "../lib/autosave";

const emptyPart = { name: "", description: "", part_number: "", supplier: "", cost: "", photo_path: null };

export function Parts() {
  const parts = useParts();
  const nav = useNavigate();
  const { seeMoney } = usePerms();
  const [s, setS] = useState("");
  const data = useMemo(() => (parts.data ?? []).filter((p) => `${p.name} ${p.part_number} ${p.supplier}`.toLowerCase().includes(s.toLowerCase())), [parts.data, s]);
  return (
    <Page title="Parts" subtitle={`${parts.data?.length ?? 0} commonly used parts`} wide testid="parts-page"
      right={<Btn icon={Plus} onClick={() => nav("/parts/new")} data-testid="add-part-button">New Part</Btn>}>
      <SearchBox testid="parts-search-input" value={s} onChange={setS} placeholder="Search name, number or supplier" />
      {parts.isLoading ? <Loading /> : !data.length ? <Empty icon={Package} title="No parts yet" subtitle="Add the parts your crews use most." /> : (
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="parts-list">
          {data.map((p) => (
            <Link key={p.id} to={`/parts/${p.id}`} data-testid={`part-card-${p.id}`} className="overflow-hidden rounded-2xl border border-wf-line transition-[border-color,transform] hover:-translate-y-0.5 hover:border-wf-brand2">
              {p.photo_path ? <img src={fileUrl(p.photo_path)} alt={p.name} className="aspect-[4/3] w-full bg-wf-s3 object-cover" /> : (
                <div className="flex aspect-[4/3] items-center justify-center bg-wf-s2 text-wf-line2"><Package size={40} /></div>
              )}
              <div className="p-4">
                <p className="truncate font-bold">{p.name}</p>
                <p className="truncate text-sm text-wf-mute">{[p.part_number && `#${p.part_number}`, p.supplier].filter(Boolean).join(" · ") || "—"}</p>
                {seeMoney && !!p.cost && <p className="mt-1 font-extrabold text-wf-brand">{money(p.cost)}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

export function PartForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isManager } = usePerms();
  const existing = useQuery({ queryKey: ["part", id], queryFn: () => api(`/parts/${id}`), enabled: !!id });
  const sup = useSuggestions("supplier");
  const [f, setF] = useState(emptyPart);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    const p = existing.data;
    if (p && !loaded) {
      setF({ name: p.name, description: p.description ?? "", part_number: p.part_number, supplier: p.supplier, cost: p.cost ? String(p.cost) : "", photo_path: p.photo_path ?? null });
      setLoaded(true);
    }
  }, [existing.data, loaded]);
  const auto = useAutoSave(f, async (d) => {
    await api(`/parts/${id}`, { method: "PUT", body: { ...d, cost: parseFloat(d.cost) || 0 } });
    qc.invalidateQueries({ queryKey: ["parts"] });
  }, { ready: !!id && loaded, enabled: !!f.name.trim() });
  const draft = useDraft("draft:part:new", f, setF, !id);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter the part name");
    setSaving(true);
    try {
      await api(id ? `/parts/${id}` : "/parts", { method: id ? "PUT" : "POST", body: { ...f, cost: parseFloat(f.cost) || 0 } });
      if (!id) draft.clear();
      qc.invalidateQueries({ queryKey: ["parts"] });
      toast.success(id ? "Part updated" : "Part added");
      nav("/parts");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api(`/parts/${id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["parts"] });
      nav("/parts");
    } catch (e) {
      toast.error(e.message);
    }
  };
  const photo = async (files) => {
    setUploading(true);
    try {
      set("photo_path")(await uploadFile(files[0]));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };
  if (id && existing.isLoading) return <Loading />;
  return (
    <Page title={id ? "Edit Part" : "New Part"} back testid="part-form"
      right={<>
        {id && <IconBtn icon={Trash2} tone="danger" onClick={() => setConfirm(true)} data-testid="part-delete-button" />}
        {id ? <SaveStatus status={auto.status} /> : <Btn icon={Save} loading={saving} onClick={save} data-testid="part-save-button">Save</Btn>}
      </>}>
      <DraftBanner draft={draft} onDiscard={() => { draft.clear(); setF(emptyPart); }} />
      <div className="grid max-w-4xl gap-8 md:grid-cols-[260px_1fr]">
        <div>
          {f.photo_path ? <img src={fileUrl(f.photo_path)} alt="" className="mb-3 aspect-square w-full rounded-2xl bg-wf-s3 object-cover" /> : (
            <div className="mb-3 flex aspect-square w-full items-center justify-center rounded-2xl border border-dashed border-wf-line2 text-wf-mute"><Package size={44} /></div>
          )}
          <FilePickButton testid="part-photo-button" loading={uploading} onFiles={photo} className="w-full">{f.photo_path ? "Change photo" : "Add photo"}</FilePickButton>
          {f.photo_path && <Btn variant="ghost" className="mt-2 w-full" onClick={() => set("photo_path")(null)} data-testid="part-photo-remove">Remove photo</Btn>}
        </div>
        <div>
          <Field label="Part Name *" testid="part-name-input" value={f.name} onChange={set("name")} placeholder="HID iCLASS SE reader" />
          <Field label="Part Description" testid="part-description-input" value={f.description} onChange={set("description")} multiline />
          <Field label="Part Number" testid="part-number-input" value={f.part_number} onChange={set("part_number")} />
          <SuggestField suggestions={sup.data} label="Supplier" testid="part-supplier-input" value={f.supplier} onChange={set("supplier")} />
          {isManager && <Field label="Cost ($)" type="number" step="0.01" testid="part-cost-input" value={f.cost} onChange={set("cost")} placeholder="0.00" />}
        </div>
      </div>
      <Confirm open={confirm} title="Delete this part?" message="Past work order entries keep their part details." onConfirm={remove} onClose={() => setConfirm(false)} />
    </Page>
  );
}
